import React, { forwardRef } from "react";
import PropTypes from "prop-types";
import { Link as GatsbyLink } from "gatsby";
import { NoibuLink } from "@sunwing/shared-components-beta";
import { RCL as useTranslation } from "../../components/RCL";
import { cloudinaryGenURL } from "../../utils/cloudinaryHelpers";
import { ImageConnector } from "../ImageConnector";
import { Link } from "../../components/Link";

const INTERNAL_URL_REGEX = new RegExp(
	"^(https?://(www.)?vacanceswestjetquebec.com)(/)?|^/(?!/)|^#$"
);

const LinkConnector = forwardRef(
	(
		{
			to,
			target,
			children,
			image,
			imageAltText,
			icon,
			theme,
			noStyle,
			showExternalIcon,
			activeClassName,
			partiallyActive,
			externalModalMessage,
			inlineLink,
			...props
		},
		ref
	) => {
		const isInternalLink = INTERNAL_URL_REGEX.test(to);
		let linkChildren = children;

		if (image?.[0]) {
			linkChildren = (
				<ImageConnector
					image={cloudinaryGenURL({ cloudinaryImage: image[0] })}
					alt={imageAltText}
				/>
			);
		}

		const translations = {
			title: useTranslation({ searchKey: "noibu-link-title" }),
			loadingTitle: useTranslation({ searchKey: "noibu-link-loading" }),
			modalLabels: {
				title: useTranslation({ searchKey: "noibu-link-modal-title" }),
				unavailableMessage: useTranslation({ searchKey: "noibu-link-unavailable" }),
			},
		};

		if (to === "#helpcode") {
			return (
				<NoibuLink
					className={props.className}
					title={translations.title}
					loadingTitle={translations.loadingTitle}
					modalLabels={{
						title: translations.modalLabels.title,
						unavailableMessage: translations.modalLabels.unavailableMessage,
					}}
				/>
			);
		}

		if (
			(isInternalLink ||
				to?.startsWith("#") ||
				to?.startsWith("tel:") ||
				to?.startsWith("mailto:")) &&
			target !== "_blank"
		) {
			const href = to.replace(INTERNAL_URL_REGEX, "/");
			return (
				<Link
					theme={theme}
					icon={icon}
					noStyle={noStyle}
					to={href}
					target={target === null ? "_self" : target}
					inlineLink={inlineLink ?? false}
					renderLink={(_to, linkProps) => (
						<GatsbyLink
							to={_to}
							activeClassName={activeClassName}
							partiallyActive={partiallyActive}
							// eslint-disable-next-line react/jsx-props-no-spreading
							{...linkProps}
						/>
					)}
					// eslint-disable-next-line react/jsx-props-no-spreading
					{...props}>
					{linkChildren}
				</Link>
			);
		}

		return (
			<Link
				ref={ref}
				to={to}
				icon={icon}
				// eslint-disable-next-line react/jsx-props-no-spreading
				{...(!isInternalLink &&
					showExternalIcon && {
						rel: "noopener",
						icon: "external-micro",
					})}
				// Force _blank for this specific inline entry use-case
				target={!isInternalLink && target === null ? "_blank" : target}
				theme={theme}
				noStyle={noStyle}
				showExternalIcon={showExternalIcon}
				inlineLink={inlineLink ?? false}
				// eslint-disable-next-line react/jsx-props-no-spreading
				{...props}>
				{linkChildren}
			</Link>
		);
	}
);

LinkConnector.propTypes = {
	to: PropTypes.string.isRequired,
	target: PropTypes.oneOf(["_self", "_blank", "_parent", "_top"]),
	image: PropTypes.arrayOf(
		PropTypes.shape({
			alt: PropTypes.string,
			title: PropTypes.string,
			width: PropTypes.number,
			height: PropTypes.number,
			data: PropTypes.shape({
				src: PropTypes.string,
				srcSet: PropTypes.string,
				sizes: PropTypes.string,
			}),
		})
	),
	imageAltText: PropTypes.string,
	activeClassName: PropTypes.string,
	partiallyActive: PropTypes.bool,
	className: PropTypes.string,
	children: PropTypes.node.isRequired,
	icon: PropTypes.string,
	theme: PropTypes.oneOf(["primary", "inverse"]),
	noStyle: PropTypes.bool,
	showExternalIcon: PropTypes.bool,
	externalModalMessage: PropTypes.string,
	isHelpCode: PropTypes.bool,
	inlineLink: PropTypes.bool,
};

LinkConnector.defaultProps = {
	target: "_self",
	image: undefined,
	imageAltText: undefined,
	activeClassName: undefined,
	className: undefined,
	partiallyActive: false,
	icon: undefined,
	theme: "primary",
	noStyle: false,
	showExternalIcon: true,
	externalModalMessage: undefined,
	isHelpCode: undefined,
	inlineLink: false,
};

export { LinkConnector };
